import { Form } from '@form/Form';
import FormSubmit from '@form/FormSubmit';
import { Accordion } from '@ui/Accordion/Accordion';
import Loader from 'components/Loader';
import SelectComponent from 'components/SelectComponent';
import DotObject from 'dot-object';
import { groupBy, merge, set } from 'lodash';
import React, { Key, useCallback, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { useApi } from '../../../../hooks/useApi';
import { PermissionGroup, Relative } from '../types';
import PermissionInputList from './PermissionInputList';
import PredefinedPermissionGroups from './PredefinedPermissionGroups';

interface CommonProps {
    clubSlug: string;
    callback?: () => void;
}

interface GroupProps extends CommonProps {
    permissionGroup: PermissionGroup;
    relative?: never;
}

interface RelativeProps extends CommonProps {
    permissionGroup?: never;
    relative: Relative;
}

export const PermissionForm: React.FunctionComponent<
    React.PropsWithChildren<React.PropsWithChildren<RelativeProps | GroupProps>>
> = ({ clubSlug, permissionGroup, relative, callback }) => {
    const { t } = useTranslation('general');
    const [isLoading, setIsLoading] = useState(false);
    const [currentPreset, setCurrentPreset] = useState<Key>();
    const api = useApi();
    const { data, isLoading: isLoadingPermissions } = useSWR<{
        data: {
            permissions: { [x: string]: string };
            presets: { label: string; permissions: string[]; key: string }[];
        };
    }>(`club/${clubSlug}/permissions`);

    const permissions = useMemo(
        (): { [x: string]: [string, string][] } =>
            groupBy(Object.entries(data?.data.permissions || {}), ([value]) => value.split('.')[0]),
        [data]
    );

    // The endpoint already drops the rights of modules this club has hidden, so this is the
    // authoritative list of what may be rendered. Rights that are stored but no longer listed
    // survive untouched: they come in through the defaultValues below and are handed back on submit.
    const availablePermissions = useMemo(() => Object.keys(data?.data.permissions || {}), [data]);

    const transformPermissions = useCallback(
        (permissions: { [x: string]: null | boolean } = {}) => {
            const allFalsePermissions = Object.entries(data?.data.permissions || {}).reduce(
                (carry, [permission]) => set(carry, permission, null),
                {}
            );

            if (!permissions || Array.isArray(permissions) || Object.keys(permissions).length <= 0)
                return allFalsePermissions;
            return merge(allFalsePermissions, DotObject.object(permissions));
        },
        [data]
    );

    const currentPresetValues = useMemo(
        () =>
            transformPermissions(
                (data?.data.presets.find(({ key }) => key === currentPreset)?.permissions || []).reduce(
                    (carry, item) => ({ ...carry, [item]: true }),
                    {}
                )
            ),
        [currentPreset, transformPermissions, data]
    );

    const handleSubmit = (data) => {
        setIsLoading(true);
        return api({
            url: `club/${clubSlug}/permissions`,
            method: 'PUT',
            data: {
                user: relative?.id,
                group: permissionGroup?.id,
                permissions: Object.fromEntries(
                    Object.entries(DotObject.dot(data)).filter(([, value]) => value !== null)
                )
            },
            onBefore: () => setIsLoading(false),
            onSuccess: callback
        });
    };

    return (
        <Loader isLoading={isLoadingPermissions}>
            <p className="tw-text-center tw-text-accents-6">{t('permissions.form.intro')}</p>
            <p className="tw-text-left tw-text-accents-6">
                {t('permissions.form.levelsIntro')} <br />
                <div className="tw-my-4 tw-grid tw-grid-cols-2 tw-gap-2">
                    <b>{t('permissions.common.allow')}</b>
                    <span className="tw-col-start-2">{t('permissions.form.allowDescription')}</span>
                    <b>{t('permissions.common.deny')}</b>
                    <span className="tw-col-start-2">{t('permissions.form.denyDescription')}</span>
                    <b>{t('permissions.form.neither')} </b>
                    <span className="tw-col-start-2">{t('permissions.form.neitherDescription')}</span>
                </div>
            </p>
            <p className="tw-mb-6 tw-text-center tw-text-accents-6">
                <Trans
                    t={t}
                    i18nKey="permissions.form.priorityNote"
                    components={{ b1: <b />, b2: <b /> }}
                />
            </p>
            <SelectComponent
                options={data?.data.presets.map(({ label, key }) => ({ label, value: key }))}
                value={currentPreset}
                onChange={setCurrentPreset}
            />
            <Form
                onSubmit={handleSubmit}
                defaultValues={transformPermissions(
                    currentPreset ? currentPresetValues : relative?.permissions || permissionGroup?.permissions
                )}
                setDefaultValuesOnRerender
            >
                <div>
                    <Accordion className="tw-mt-3" title={t('permissions.form.standardOptions')}>
                        <PredefinedPermissionGroups availablePermissions={availablePermissions} />
                    </Accordion>
                    <Accordion title={t('permissions.form.fullOptions')}>
                        <PermissionInputList permissions={permissions} />
                    </Accordion>
                </div>
                <FormSubmit isLoading={isLoading} />
            </Form>
        </Loader>
    );
};
